import { useControlled } from '@mui/material/utils';
import * as ReactSentry from '@sentry/react';
import produce from 'immer';
import { get, isEqual, uniq } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';

import { useForceUpdate } from '@work4all/utils/lib/hooks/use-force-update';

import { INDIVIDUAL_TAB_ID } from '../mask-overlay/components/custom-fields/contants';

interface BoundUnregisterTabFormInputFunction {
  (): void;
}

interface UnregisterTabFormInputFunction {
  (tab: string | number, name: string): void;
}

interface RegisterTabFormInputFunction {
  (tab: string | number, name: string): BoundUnregisterTabFormInputFunction;
}

export interface MaskTabContextValue {
  value: string | number;
  onChange?: (value: string | number) => void;
  errors: Record<string, number>;
  register: RegisterTabFormInputFunction;
  unregister: UnregisterTabFormInputFunction;
}

export interface MaskTabContextProps {
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (value: string | number) => void;
  children?: React.ReactNode;
}

type TabFields = Record<string, string[]>;

type StateUpdateFunction = (value: TabFields) => void;

const Context = createContext<MaskTabContextValue | null>(null);

export function MaskTabContext(props: MaskTabContextProps) {
  const { onChange, defaultValue: defaultTab, children } = props;

  const [value, setValue] = useControlled({
    controlled: props.value,
    default: defaultTab,
    name: 'MaskTabContext',
    state: 'value',
  });

  const handleChange = useCallback(
    (value: string | number) => {
      setValue(value);
      onChange?.(value);
    },
    [setValue, onChange]
  );

  const form = useFormContext();

  const [fields, setFields] = useState<TabFields>({});
  const forceUpdate = useForceUpdate();

  const updateQueueRef = useRef<StateUpdateFunction[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (updateQueueRef.current.length > 0) {
      const updates = updateQueueRef.current;
      updateQueueRef.current = [];

      const newFields = produce(fields, (draft) => {
        for (const update of updates) {
          update(draft);
        }
      });

      if (!isEqual(fields, newFields)) {
        setFields(newFields);
      }
    }
  });

  const scheduleUpdate = useCallback(
    (update: StateUpdateFunction) => {
      updateQueueRef.current.push(update);
      forceUpdate();
    },
    [forceUpdate]
  );

  const register = useCallback<RegisterTabFormInputFunction>(
    (tab: string | number, name: string) => {
      scheduleUpdate(addTabField(tab, name));

      const unregister = () => {
        scheduleUpdate(removeTabField(tab, name));
      };

      return unregister;
    },
    [scheduleUpdate]
  );

  const unregister = useCallback<UnregisterTabFormInputFunction>(
    (tab, name) => {
      scheduleUpdate(removeTabField(tab, name));
    },
    [scheduleUpdate]
  );

  const formState = form?.formState;
  const formErrors = formState?.errors;

  // Track last reported set of missing-field validation errors to avoid duplicates
  const lastReportedMissingFieldsRef = useRef<string[]>([]);

  // Compute per-tab error counts and fields with errors not present in any tab
  const computed = useComputedErrors(fields, formErrors, defaultTab);

  // Report to Sentry once per distinct set of missing fields
  useEffect(() => {
    const currentSorted = uniq(computed.missing).sort();
    const prev = lastReportedMissingFieldsRef.current;

    if (currentSorted.length === 0) {
      // Reset tracker if no missing fields are present
      lastReportedMissingFieldsRef.current = currentSorted;
      return;
    }

    if (!isEqual(prev, currentSorted)) {
      const error = new Error(
        'Mask validation error(s) on fields not present in any mask tab'
      );

      ReactSentry.captureException(error, (scope) => {
        scope.setTag('issue.type', 'missing_mask_field_validation');
        scope.setExtra('missingFields', currentSorted);
        scope.setExtra('tabs', Object.keys(fields));
        scope.setExtra('fieldsByTab', fields);
        scope.setExtra('activeTab', value);
        scope.setExtra('defaultTab', defaultTab);
        scope.setExtra('rawErrors', formErrors);
        return scope;
      });

      console.error(error);

      lastReportedMissingFieldsRef.current = currentSorted;
    }
    // We intentionally depend on fields/value/defaultTab to provide better context in events,
    // but only emit when the set of missing fields actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed.missing, fields, value, defaultTab, formErrors]);

  // TODO: WW-6471 There are sometimes performance issues with this because the context
  // value is changed every time the `form` is updated. We should find a better
  // way to do this, so that it only updates when the errors change.
  const context = useMemo<MaskTabContextValue>(() => {
    return {
      value,
      onChange: handleChange,
      errors: computed.errors,
      register,
      unregister,
    };
  }, [value, handleChange, register, unregister, computed]);

  return <Context.Provider value={context}>{children}</Context.Provider>;
}

/**
 * Returns the context value of the MaskTabContext. If this hook is called
 * outside of a MaskTabContext, it will throw.
 */
export function useMaskTabContext(): MaskTabContextValue {
  const context = useContext(Context);

  if (context === null) {
    throw new Error('useMaskTabContext must be used within a <MaskTabContext>');
  }

  return context;
}

function addTabField(tab: string | number, name: string) {
  return (draft: TabFields) => {
    draft[tab] = draft[tab] ?? [];
    draft[tab].push(name);
  };
}

function removeTabField(tab: string | number, name: string) {
  return (draft: TabFields) => {
    // TODO If there are duplicate inputs in the same tab, this will
    // unregister all of them when one is unregistered. This is not an
    // issue right now, as there are no masks at the moment with duplicate
    // inputs in the same tab. But it might be a problem in the future. In
    // that case, this should be changed to unregister only the one that
    // actually called the `unregister` function.
    draft[tab] = draft[tab]?.filter((n) => n !== name);
  };
}

function getAllFieldsWithErrors(
  errors: Record<string, unknown> | undefined
): string[] {
  if (!errors) return [];

  // Custom field errors are nested inside the `_customFields` property. All
  // other errors are flat keys in the `errors` object.
  const { _customFields = {}, ...otherFields } = errors;

  return [
    ...Object.keys(_customFields).map((name) => `_customFields.${name}`),
    ...Object.keys(otherFields),
  ];
}

function useComputedErrors(
  fields: TabFields,
  formErrors: Record<string, unknown>,
  defaultTab: string | number
) {
  return useMemo(() => {
    // Keep track of all errors registered to the tabs. This way we can find
    // errors that are not displayed in the form UI and still show them in the
    // error badge for the default tab.
    //
    // Such errors are usually a configuration error (either in the form or in
    // the validation schema), but this way they are at least displayed
    // somewhere, instead of a user just not being able to submit the form with
    // no explanation in the UI.
    //
    // This requires the developers to configure the default tab with the
    // "defaultValue" prop.
    const errorsRegisteredToTabs = new Set<string>();

    const errors = Object.keys(fields).reduce<Record<string, number>>(
      (errors, tab) => {
        // uniq is for count field error once, even if we use same field more than one on the tab
        // it should show 1 error per field in each tab
        const tabErrors = uniq(fields[tab]).reduce<number>(
          (tabErrors, name) => {
            const error = get(formErrors, name);

            if (error) {
              errorsRegisteredToTabs.add(name);
              return tabErrors + 1;
            }

            return tabErrors;
          },
          0
        );

        errors[tab] = tabErrors;

        return errors;
      },
      {}
    );

    // Determine fields that have errors but are not registered to any tab
    const allFieldsWithErrors = getAllFieldsWithErrors(formErrors);
    const missing = allFieldsWithErrors.filter(
      (fieldName) => !errorsRegisteredToTabs.has(fieldName)
    );

    // Add missing errors to appropriate tabs for badge display
    let customCount = 0;
    let otherCount = 0;
    for (const name of missing) {
      if (name.startsWith('_customFields.')) customCount++;
      else otherCount++;
    }

    if (customCount > 0) {
      errors[INDIVIDUAL_TAB_ID] ??= 0;
      errors[INDIVIDUAL_TAB_ID] += customCount;
    }
    if (otherCount > 0) {
      errors[defaultTab] ??= 0;
      errors[defaultTab] += otherCount;
    }

    return { errors, missing };
  }, [fields, formErrors, defaultTab]);
}
